<template>
  <base-page-layout title="Decomposição Faturamento" :filters="['lojas', 'segmentos', 'produtos']" hide-ecommerce-configuration>
    <template #header>
      <export-data button-label="Exportar dados por loja" :data-fetcher="getDataByUnitToExport" filename="decomposicao_faturamento" ghost />
    </template>
    <decomposition-filters v-if="lastUpdate.last_updated" v-model="filters" :last-updated="formatedLastUpdated" />
    <decomposition-cards
      ref="decompositionCards"
      :currentData="currentData"
      :referencyData="referencyData"
      :loadingInfos="loading"
      @open-details="openDetails"
    />
    <decomposition-details
      :show-details="showDetails"
      :detail-indicator="detailIndicator"
      :detail-info="detailInfo"
      :current-date="filters.currentDate"
      :referency-date="filters.referencyDate"
      :units="stateFilters.units"
      :units-configuration="stateFilters.unitsConfiguration"
      :products="stateFilters.products"
      :segments="stateFilters.segments"
      :segments-object="stateFilters.segmentsObjects"
    />
  </base-page-layout>
</template>

<script>
import { QUERY_GET_METRICS, QUERY_GET_METRICS_BY_UNIT } from '../../graphql'
import { mapState } from 'vuex'
import { DateTime } from 'luxon'

export default {
  name: 'DecompositionRevenues',
  components: {
    ExportData: () => import('@/components/export-data/ExportData.vue'),
    BasePageLayout: () => import('@/components/BasePageLayout.vue'),
    DecompositionFilters: () => import('../components/DecompositionFilters.vue'),
    DecompositionCards: () => import('../components/DecompositionCards.vue'),
    DecompositionDetails: () => import('../components/DecompositionDetails.vue'),
  },
  data: () => ({
    showDetails: false,
    loading: true,
    detailIndicator: '',
    currentData: {},
    detailInfo: {},
    referencyData: {},
    filters: {
      referencyDate: [],
      currentDate: [],
    },
  }),
  computed: {
    ...mapState({
      lastUpdate: (state) => state.lastUpdate,
      stateFilters: (state) => state.filters,
    }),
    dateValidations() {
      const { referencyDate, currentDate } = this.filters
      const allDates = referencyDate.concat(currentDate)

      return !allDates.includes('') && allDates.length === 4
    },
    formatedLastUpdated() {
      if (!this.lastUpdate.last_updated) return ''
      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM-dd')
    },
    firstDayOfLastMonth() {
      if (!this.lastUpdate.last_updated) return ''
      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').plus({ months: -1 }).startOf('month').toFormat('yyyy-MM-dd')
    },
    firstDayReferencyMonth() {
      if (!this.lastUpdate.last_updated) return ''
      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').plus({ months: -1 }).toFormat('yyyy-MM-dd')
    },
    firstDayOfCurrentMonth() {
      if (!this.lastUpdate.last_updated) return ''
      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').startOf('month').toFormat('yyyy-MM-dd')
    },
    lastDay() {
      if (this.lastUpdate.last_updated) return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM-dd')
      return ''
    },
  },
  watch: {
    filters: {
      handler() {
        if (this.dateValidations) {
          this.waitLoadMetrics()
        }
      },
      deep: true,
    },
    'stateFilters.units': {
      handler() {
        if (this.dateValidations) {
          this.waitLoadMetrics()
        }
      },
    },
    'stateFilters.segments': {
      handler() {
        if (this.dateValidations) {
          this.waitLoadMetrics()
        }
      },
    },
    'stateFilters.products': {
      handler() {
        if (this.dateValidations) {
          this.waitLoadMetrics()
        }
      },
    },
    'lastUpdate.last_updated': {
      handler() {
        this.filters.referencyDate = [this.firstDayOfLastMonth, this.firstDayReferencyMonth]
        this.filters.currentDate = [this.firstDayOfCurrentMonth, this.lastDay]
      },
      immediate: true,
    },
  },
  methods: {
    async waitLoadMetrics() {
      this.loading = true
      this.showDetails = false
      const { units, segments } = this.stateFilters
      if (units !== null && segments !== null) {
        this.detailIndicator = ''
        try {
          this.currentData = await this.getCurrentMetrics()
          this.referencyData = await this.getReferencyMetrics()
        } catch (error) {
          this.$snackbar({ message: `Erro ao buscar os dados.` })
        }
        this.loading = false
      }
    },
    getCurrentMetricsVariables() {
      const { currentDate } = this.filters
      const { units, segments, products } = this.stateFilters

      return {
        initialDate: currentDate[0],
        finalDate: currentDate[1],
        units,
        products: products || [],
        segments: segments || [],
      }
    },
    getCurrentMetrics() {
      const variables = this.getCurrentMetricsVariables()
      return this.getMetricData(variables)
    },
    getReferencyMetricsVariables() {
      const { referencyDate } = this.filters
      const { units, segments, products } = this.stateFilters

      return {
        initialDate: referencyDate[0],
        finalDate: referencyDate[1],
        units,
        products: products || [],
        segments: segments || [],
      }
    },
    getReferencyMetrics() {
      const variables = this.getReferencyMetricsVariables()
      return this.getMetricData(variables)
    },
    getMetricData(variables) {
      if (!variables.units.length) return {}
      return this.$apollo
        .query({
          query: QUERY_GET_METRICS,
          variables,
          fetchPolicy: 'no-cache',
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
        })
        .then(({ data: { metricsKpi } }) => {
          return metricsKpi
        })
        .catch((error) => {
          console.error(error)
          this.$snackbar({ message: `Erro ao buscar os dados.` })
        })
    },
    getCurrentMetricsByUnit() {
      const variables = this.getCurrentMetricsVariables()
      return this.getMetricByUnitData(variables)
    },
    getReferencyMetricsByUnit() {
      const variables = this.getReferencyMetricsVariables()
      return this.getMetricByUnitData(variables)
    },
    getMetricByUnitData(variables) {
      if (!variables.units.length) return
      return this.$apollo
        .query({
          query: QUERY_GET_METRICS_BY_UNIT,
          variables,
          fetchPolicy: 'no-cache',
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
        })
        .then(({ data: { metricsKpiByUnits } }) => {
          return metricsKpiByUnits
        })
        .catch((error) => {
          console.error(error)
          this.$snackbar({ message: `Erro ao buscar os dados.` })
        })
    },
    openDetails(detailInfo) {
      const { detailOpen, indicator } = detailInfo

      this.showDetails = detailOpen
      this.detailIndicator = indicator
      this.detailInfo = detailInfo
    },
    getPctChange(current, referency) {
      if (!referency) return 0
      return Number(((current - referency) / referency).toFixed(4))
    },
    async getDataByUnitToExport() {
      const periodHeaders = [
        'Faturamento',
        'Ticket Médio',
        'Cupons',
        'Média de Itens na Cesta',
        'Valor Médio dos Produtos',
        'Estimativa de Clientes Únicos',
        'Média de Compras por Cliente',
      ]

      const headers = [
        'ID Loja',
        'CNPJ',
        'Nome',
        ...periodHeaders.map((header) => [`${header} (Referencial)`, `${header} (Atual)`, `${header} (Variação)`]).flat(),
      ]

      const currentMetricsByUnit = await this.getCurrentMetricsByUnit()
      const referencyMetricsByUnit = await this.getReferencyMetricsByUnit()

      const data = currentMetricsByUnit.map((item) => {
        const referencyItem = referencyMetricsByUnit.find((referencyItem) => referencyItem.unit.id_erp === item.unit.id_erp)

        return [
          item.unit?.id_erp,
          item.unit?.cnpj,
          item.unit?.name,
          // Revenue
          referencyItem?.metrics?.revenue?.[0]?.value,
          item.metrics?.revenue?.[0]?.value,
          this.getPctChange(item.metrics?.revenue?.[0]?.value, referencyItem?.metrics?.revenue?.[0]?.value),
          // Average Ticket
          referencyItem?.metrics?.average_ticket?.[0]?.value,
          item.metrics?.average_ticket?.[0]?.value,
          this.getPctChange(item.metrics?.average_ticket?.[0]?.value, referencyItem?.metrics?.average_ticket?.[0]?.value),
          // Total Coupons
          referencyItem?.metrics?.total_coupons?.[0]?.value,
          item.metrics?.total_coupons?.[0]?.value,
          this.getPctChange(item.metrics?.total_coupons?.[0]?.value, referencyItem?.metrics?.total_coupons?.[0]?.value),
          // Average Items in Basket
          referencyItem?.metrics?.average_items_in_basket?.[0]?.value,
          item.metrics?.average_items_in_basket?.[0]?.value,
          this.getPctChange(item.metrics?.average_items_in_basket?.[0]?.value, referencyItem?.metrics?.average_items_in_basket?.[0]?.value),
          // Average Product Value
          referencyItem?.metrics?.average_product_value?.[0]?.value,
          item.metrics?.average_product_value?.[0]?.value,
          this.getPctChange(item.metrics?.average_product_value?.[0]?.value, referencyItem?.metrics?.average_product_value?.[0]?.value),
          // Estimated Unique Clients
          referencyItem?.metrics?.estimated_total_number_of_customers?.[0]?.value,
          item.metrics?.estimated_total_number_of_customers?.[0]?.value,
          this.getPctChange(
            item.metrics?.estimated_total_number_of_customers?.[0]?.value,
            referencyItem?.metrics?.estimated_total_number_of_customers?.[0]?.value
          ),
          // Average Purchases per Client
          referencyItem?.metrics?.average_customer_coupons?.[0]?.value,
          item.metrics?.average_customer_coupons?.[0]?.value,
          this.getPctChange(item.metrics?.average_customer_coupons?.[0]?.value, referencyItem?.metrics?.average_customer_coupons?.[0]?.value),
        ]
      })

      return { headers, rows: data }
    },
  },
}
</script>

<style lang="scss">
$text-bold: 700;

.input-text {
  color: $marca-0;
  &_title {
    word-break: break-word;
    font-weight: $text-bold;
    font-size: 22px;
  }
  &_subtitle {
    font-size: 16px;
    word-break: break-word;
    line-height: 1.5;
  }
  &_detail {
    font-weight: $text-bold;
    font-size: 14px;
  }
}

.decomposition-title {
  font-size: 36px;
  color: $marca-0;
}
.decomposition-subtitle {
  font-size: 18px;
  color: $marca-0;
}
.text-date {
  font-size: 18px;
}
.description {
  font-size: 12px;
}
</style>
